<template>
    <div class="select-container" :data-label-placement="labelPlacement" :style="{ '--select-min-width': minWidth + 'px' }">
        <label v-if="label" :for="id" class="select-label">{{ label }}</label>
        <div ref="select" class="select-wrapper">
            <button :id="id" class="select" type="button" :aria-expanded="isOpen.toString()" aria-haspopup="listbox"
                @click="togglePanel" @keydown.esc="closePanel">
                <span class="select-value">
                    <template v-if="selectedOptions.length === 0">
                        <span class="select-value__placeholder">{{ placeholder }}</span>
                    </template>
                    <template v-else>
                        <div class="select-value__list">
                            <span class="item" v-for="(option, index) in visibleOptions" :key="option.value">
                                <slot name="optionLabel" :option="option">
                                    {{ option.label }}
                                </slot>
                                <span v-if="index < visibleOptions.length - 1" class="item__separator">,</span>
                            </span>

                            <span class="item-more" v-if="selectedOptions.length > maxVisibleOptions">
                                +{{ selectedOptions.length - maxVisibleOptions }}
                            </span>
                        </div>
                    </template>
                </span>
                <ArrowDownSvg class="select-arrow" :class="{ 'select-arrow--open': isOpen }" />
            </button>

            <div v-if="isOpen" class="select-panel" role="listbox"
                :aria-multiselectable="type === 'multiple' ? 'true' : null"
                :style="{ '--panel-max-height': panelMaxHeight + 'px' }">
                <template v-for="(item, index) in orderedOptions">
                    <section v-if="isSection(item)" :key="`section-${index}`" class="select-section">
                        <span class="select-section__title">{{ item.label }}</span>
                        <div v-for="(child, childIndex) in getOrderedChildren(item)" :key="child.value"
                            class="select-option select-option--child" role="option"
                            :aria-selected="isSelected(child.value).toString()" @click="selectOption(child.value)">
                            <checkboxUI v-if="type === 'multiple'"
                                :id="`${id || 'multiple-select'}-section-${index}-option-${childIndex}`"
                                :checked="isSelected(child.value)" @change="toggleOption(child.value)">
                                <template #label>
                                    <slot name="optionLabel" :option="child">
                                        {{ child.label }}
                                    </slot>
                                </template>
                            </checkboxUI>
                            <span v-else class="select-option__single-label">
                                <slot name="optionLabel" :option="child">
                                    {{ child.label }}
                                </slot>
                                <CircleCheckIconSvg v-if="isSelected(child.value)" class="select-option__check-icon" />
                            </span>
                        </div>
                    </section>

                    <div v-else :key="`option-${item.value}`" class="select-option" role="option"
                        :aria-selected="isSelected(item.value).toString()" @click="selectOption(item.value)">
                        <checkboxUI v-if="type === 'multiple'" :id="`${id || 'multiple-select'}-option-${index}`"
                            :checked="isSelected(item.value)"
                            @change="toggleOption(item.value)">
                            <template #label>
                                <slot name="optionLabel" :option="item">
                                    {{ item.label }}
                                </slot>
                            </template>
                        </checkboxUI>
                        <span v-else class="select-option__single-label">
                            <slot name="optionLabel" :option="item">
                                {{ item.label }}
                            </slot>
                            <CircleCheckIconSvg v-if="isSelected(item.value)" class="select-option__check-icon" />
                        </span>
                    </div>
                </template>
                <span v-if="options.length === 0" class="select-panel__empty">
                    Nenhuma opção disponível
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import ArrowDownSvg from '@/assets/icons/arrow-down.svg';
import CircleCheckIconSvg from '@/assets/icons/circle-check.svg';
import checkboxUI from '@/components/ui/checkbox.vue';

const hasValidOrderIndex = option =>
    option.orderIndex === undefined || typeof option.orderIndex === 'number';

const isValidIndividualOption = option =>
    option !== null &&
    typeof option === 'object' &&
    'value' in option &&
    'label' in option &&
    hasValidOrderIndex(option);

const isValidOption = option => {
    if (option === null || typeof option !== 'object' || !('label' in option) || !hasValidOrderIndex(option)) {
        return false;
    }

    if (Array.isArray(option.children)) {
        return option.children.every(isValidIndividualOption);
    }

    return 'value' in option;
};

export default {
    name: 'selectMultipleUI',
    components: {
        ArrowDownSvg,
        CircleCheckIconSvg,
        checkboxUI
    },
    props: {
        id: {
            type: String,
            default: null
        },
        label: {
            type: String,
            default: null
        },
        /**
         * @description Posicao do label do select. Dentro ou fora.
         */
        labelPlacement: {
            type: String,
            default: 'outside'
        },
        placeholder: {
            type: String,
            default: 'Selecione'
        },
        type: {
            type: String,
            default: 'single',
            validator: value => ['single', 'multiple'].includes(value)
        },
        options: {
            type: Array,
            required: true,
            validator: value => value.every(isValidOption)
        },
        minWidth: {
            type: Number,
            default: 180
        },
        panelMaxHeight: {
            type: Number,
            default: 150
        },
        maxVisibleOptions: {
            type: Number,
            default: 3
        },
        defaultSelected: {
            type: [Array, String, Number],
            default: () => []
        }
    },
    data() {
        return {
            isOpen: false,
            selectedValues: this.normalizeSelected(this.defaultSelected)
        }
    },
    computed: {
        visibleOptions() {
            return this.selectedOptions.slice(0, this.maxVisibleOptions);
        },
        orderedOptions() {
            return this.sortByOrderIndex(this.options);
        },
        selectableOptions() {
            return this.options.reduce((result, option) => {
                return this.isSection(option)
                    ? [...result, ...option.children]
                    : [...result, option];
            }, []);
        },
        selectedOptions() {
            return this.selectedValues
                .map(value => this.selectableOptions.find(option => option.value === value))
                .filter(Boolean);
        },
        triggerLabel() {
            if (this.selectedOptions.length === 0) {
                return this.placeholder;
            }

            const visibleLabels = this.selectedOptions
                .slice(0, 3)
                .map(option => option.label)
                .join(', ');
            const remainingCount = this.selectedOptions.length - 3;

            return remainingCount > 0
                ? `${visibleLabels} +${remainingCount}`
                : visibleLabels;
        }
    },
    watch: {
        defaultSelected(newVal) {
            this.selectedValues = this.normalizeSelected(newVal);
        }
    },
    mounted() {
        document.addEventListener('click', this.onClickOutside);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.onClickOutside);
    },
    methods: {
        normalizeSelected(value) {
            if (Array.isArray(value)) {
                return this.type === 'multiple' ? [...value] : value.slice(0, 1);
            }

            return value === null || value === undefined ? [] : [value];
        },
        isSelected(value) {
            return this.selectedValues.includes(value);
        },
        isSection(option) {
            return Array.isArray(option.children) && option.children.every(isValidIndividualOption);
        },
        sortByOrderIndex(options) {
            return options
                .map((option, originalIndex) => ({ option, originalIndex }))
                .sort((a, b) => {
                    const aOrder = typeof a.option.orderIndex === 'number'
                        ? a.option.orderIndex
                        : a.originalIndex;
                    const bOrder = typeof b.option.orderIndex === 'number'
                        ? b.option.orderIndex
                        : b.originalIndex;

                    return aOrder - bOrder || a.originalIndex - b.originalIndex;
                })
                .map(({ option }) => option);
        },
        getOrderedChildren(section) {
            return this.sortByOrderIndex(section.children);
        },
        togglePanel() {
            this.isOpen = !this.isOpen;
        },
        closePanel() {
            this.isOpen = false;
        },
        selectOption(value) {
            if (this.type === 'multiple') {
                this.toggleOption(value);
                return;
            }

            this.selectedValues = [value];
            this.$emit('change', value);
            this.closePanel();
        },
        toggleOption(value) {
            this.selectedValues = this.isSelected(value)
                ? this.selectedValues.filter(selectedValue => selectedValue !== value)
                : [...this.selectedValues, value];

            this.$emit('change', [...this.selectedValues]);
        },
        onClickOutside(event) {
            if (this.$refs.select && !this.$refs.select.contains(event.target)) {
                this.closePanel();
            }
        }
    }
}
</script>

<style scoped>

.select-container {
    display: flex;
    flex-direction: column;
    position: relative;
}

.select-wrapper {
    position: relative;
    display: inline-block;
    min-width: var(--select-min-width, 180px);
    width: max-content;
}

.select {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-width: var(--select-min-width, 180px);
    padding: var(--spacing-sm) var(--spacing-xl) var(--spacing-sm) var(--spacing-md);
    font-family: var(--font-family-primary);
    font-size: var(--text-sm);
    font-weight: 600;
    color: var(--color-secondary);
    background-color: var(--background-gray);
    border: 1px solid var(--border-primary);
    border-radius: var(--radius-md);
    cursor: pointer;
    transition: all 0.2s ease-in-out;
    appearance: none;
    outline: none;
    text-align: left;
}

.select:hover {
    box-shadow: 0 0 0 1px color-mix(in srgb, var(--color-primary) 40%, transparent);
}

.select:focus {
    box-shadow: 0 0 0 1px color-mix(in srgb, var(--color-primary) 60%, transparent);
}

.select-arrow {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    width: 16px;
    height: 16px;
    color: var(--color-primary);
    transition: transform 0.3s ease;
}

.select-arrow--open {
    transform: translateY(-50%) scaleY(-1);
}

.select-label {
    font-family: var(--font-family-primary);
    font-size: var(--text-xs);
    font-weight: 700;
    line-height: var(--leading-heading);
    color: var(--color-primary);
    text-align: left;
}

.select-container[data-label-placement="inside"] .select-label {
    position: absolute;
    left: 8px;
    top: 4px;
    font-size: var(--text-xxs);
    line-height: var(--leading-normal);
    z-index: 1;
}

.select-container[data-label-placement="inside"] .select {
    padding-top: var(--spacing-sl);
}

.select-value {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: var(--text-xs);
    color: var(--color-secondary);
}

.select-value__placeholder {
    color: var(--placeholder-color);
    font-weight: 500;
}

.select-value__list {
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);
}

.select-value__list>.item {
    display: flex;
}

.select-value__list>.item-more {
    color: var(--placeholder-color);
    font-size: var(--text-xxs);
}

.select-panel {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    z-index: 10;
    width: max-content;
    min-width: 100%;
    max-height: var(--panel-max-height, 180px);
    padding: var(--spacing-xs) 0;
    overflow-y: auto;
    background-color: var(--background-white);
    border: 1px solid var(--border-primary);
    border-radius: var(--radius-md);
    box-shadow: var(--card-box-shadow);
}

.select-option {
    padding: var(--spacing-xs) var(--spacing-sm);
    cursor: pointer;
}

.select-option:hover {
    background-color: color-mix(in srgb, var(--color-primary) 8%, transparent);
}

.select-option__single-label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--spacing-sm);
    font-size: var(--text-xs);
    font-weight: 500;
    color: var(--color-secondary);
}

.select-option__check-icon {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    color: var(--color-primary);
}

.select-section {
    padding: 4px 0;
}

.select-section+.select-section,
.select-option+.select-section {
    border-top: 1px solid var(--border-primary);
}

.select-section__title {
    display: block;
    padding: 0 var(--spacing-sm);
    font-family: var(--font-family-primary);
    font-size: var(--text-xxs);
    font-weight: 700;
    line-height: var(--leading-heading);
    text-align: left;
    text-transform: uppercase;
    letter-spacing: var(--tracking-wide);
    color: var(--color-secondary);
}

.select-option--child {
    padding-left: var(--spacing-md);
}

.select-panel__empty {
    display: block;
    padding: 6px 10px;
    font-family: var(--font-family-primary);
    font-size: var(--text-sm);
    color: var(--color-secondary);
    white-space: nowrap;
}
</style>
